import { Divider, Grid, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import { AmountWithCurrency } from 'src/shared/components/atoms/amountWithCurrency';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { useCallback, useEffect, useState } from 'react';
import { useFundraiserContext } from 'src/shared/contexts/fundraiserContext';
import { getFundraiserPaymentDistribution } from 'src/shared/services/fundraiserService';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { FundraiserFilterDatePicker } from './fundraiserFilterDatePicker';
import { FundraiserChartLine } from './fundraiserChartLine';
import { FundraisingDistributionContent } from './fundraisingDistributionContent';
import { FundraiserPaymentDistributionResponse } from '../models/fundraiserPaymentDistributionResponse';

type FundraiserDateRangePickerProps = {
  fundraiserId: number;
};

export const FundraiserDateRangePicker = (props: FundraiserDateRangePickerProps) => {
  const { fundraiserId } = props;
  const { t } = useTranslation();
  const { fundraisingDistributionSearchFilter } = useFundraiserContext();
  const isMounted = useIsMounted();
  const [loading, setLoading] = useState<boolean>(false);
  const [paymentDistribution, setPaymentDistribution] = useState<FundraiserPaymentDistributionResponse>();

  const fetchPaymentDistribution = useCallback(async () => {
    setLoading(true);

    try {
      const res = await getFundraiserPaymentDistribution(
        fundraiserId,
        DateHelper.getDate(fundraisingDistributionSearchFilter.start),
        DateHelper.getDate(fundraisingDistributionSearchFilter.end),
      );
      if (isMounted()) {
        setPaymentDistribution(res.data);
      }
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      setLoading(false);
    }
  }, [fundraiserId, fundraisingDistributionSearchFilter.end, fundraisingDistributionSearchFilter.start, isMounted]);

  useEffect(() => {
    if (fundraiserId) {
      fetchPaymentDistribution();
    }
  }, [fetchPaymentDistribution, fundraiserId]);

  return (
    <Paper sx={{ height: '100%', p: 2 }}>
      <LoadingOverlay loading={loading} />
      <Typography sx={{ fontWeight: 600 }}>{t('fundraiser:donationsForPeriod')}</Typography>
      <PageFilterSection
        leftContent={
          <Grid container spacing={2}>
            <Grid item xs={12} mt={1}>
              <Divider />
            </Grid>
            <FundraiserFilterDatePicker />
          </Grid>
        }
      />
      <Grid container p={4}>
        <Grid item xs={12} lg={8}>
          <Grid item xs={12}>
            <Typography color='textSecondary'>{t('fundraiser:revenue')}</Typography>
            <Typography sx={{ display: 'inline' }} variant='h4' fontWeight={(thm) => thm.typography.fontWeightBold}>
              <AmountWithCurrency amount={paymentDistribution?.totalAmount || 0} decimals={0} showCurrencyInSecondaryStyle currencyVariant='h5' />
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <FundraiserChartLine
              loading={loading}
              chartData={paymentDistribution?.items?.length ? paymentDistribution.items : [{ date: 'N/A', value: 0 }]}
            />
          </Grid>
        </Grid>
        <Grid item xs={12} lg={4} mb={1}>
          <FundraisingDistributionContent loading={loading} paymentDistribution={paymentDistribution} />
        </Grid>
      </Grid>
    </Paper>
  );
};
